import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import AGBComparisonLayout from '@/components/AGBComparisonLayout';
import { useLanguage } from '@/lib/i18n';
import { useSeo } from '@/hooks/useSeo';

type LegalSection = 'impressum' | 'datenschutz' | 'agb';

const Legal = () => {
  const location = useLocation();
  const { lang } = useLanguage();

  // Always show legal content in selected language
  const initialSection = useMemo<LegalSection>(() => {
    if (location.pathname.includes('datenschutz')) return 'datenschutz';
    if (location.pathname.includes('agb')) return 'agb';
    return 'impressum';
  }, [location.pathname]);

  const [activeSection, setActiveSection] = useState<LegalSection>(initialSection);
  const isAgbStandalonePage = location.pathname.includes('agb');
  const agbMeta = {
    version: 'v2026-06',
    effectiveDate: '18.06.2026',
  };

  const labels = {
    de: {
      notice:
        'Rechtlicher Hinweis: Für die rechtliche Auslegung ist die deutsche Fassung dieser Rechtstexte maßgeblich. Andere Sprachfassungen dienen nur der Information.',
      impressum: 'Impressum',
      privacy: 'Datenschutzerklärung',
      terms: 'Allgemeine Geschäftsbedingungen (AGB)',
    },
    bs: {
      notice:
        'Pravna napomena: Njemačka verzija ovih pravnih tekstova je mjerodavna za pravno tumačenje. Ostale jezičke verzije služe isključivo informativno.',
      impressum: 'Impressum',
      privacy: 'Izjava o zaštiti podataka',
      terms: 'Opći uslovi poslovanja (AGB)',
    },
    en: {
      notice:
        'Legal note: The German version of these legal texts is authoritative for legal interpretation. The English text is a plain-language version provided for information only.',
      impressum: 'Imprint',
      privacy: 'Privacy Policy',
      terms: 'General Terms and Conditions (GTC)',
    },
  };

  const ui = labels[lang];

  const legalSeoContent = {
    de: {
      impressum: {
        title: 'Impressum | ViaMedica Balkan',
        description: 'Impressum von ViaMedica Balkan mit rechtlichen Anbieterangaben und Kontaktdaten.',
      },
      datenschutz: {
        title: 'Datenschutz | ViaMedica Balkan',
        description:
          'Datenschutzerklarung von ViaMedica Balkan zur Verarbeitung personenbezogener Daten nach DSGVO und BDSG.',
      },
      agb: {
        title: 'AGB ANU und Vermittlung | ViaMedica Balkan',
        description:
          'AGB fur Direktvermittlung und Arbeitnehmeruberlassung (ANU) bei ViaMedica Balkan inklusive Leistungsumfang, Vergutung und Haftung.',
      },
    },
    bs: {
      impressum: {
        title: 'Impressum | ViaMedica Balkan',
        description: 'Impressum stranica ViaMedica Balkan sa pravnim informacijama i kontakt podacima.',
      },
      datenschutz: {
        title: 'Zastita podataka | ViaMedica Balkan',
        description: 'Izjava o zastiti podataka ViaMedica Balkan u skladu sa GDPR i BDSG pravilima.',
      },
      agb: {
        title: 'OPP za ANU i posredovanje | ViaMedica Balkan',
        description:
          'Opsti uslovi poslovanja za direktno posredovanje i ANU kod ViaMedica Balkan, ukljucujuci naknade, obaveze i odgovornost.',
      },
    },
    en: {
      impressum: {
        title: 'Imprint | ViaMedica Balkan',
        description: 'Legal imprint of ViaMedica Balkan with provider details and contact information.',
      },
      datenschutz: {
        title: 'Privacy Policy | ViaMedica Balkan',
        description:
          'Privacy policy of ViaMedica Balkan covering personal data processing under GDPR and BDSG requirements.',
      },
      agb: {
        title: 'Terms for Staffing and Placement | ViaMedica Balkan',
        description:
          'Terms and conditions for temporary staffing (ANU) and direct placement by ViaMedica Balkan, including fees, obligations, and liability.',
      },
    },
  };

  const seoByLang = legalSeoContent[lang as keyof typeof legalSeoContent] || legalSeoContent.de;
  const seoSection = seoByLang[initialSection];

  useSeo({
    title: seoSection.title,
    description: seoSection.description,
    canonical: `https://www.via-medica-balkan.de${location.pathname}`,
    keywords: 'ViaMedica Balkan, Impressum, Datenschutz, AGB, ANU, Arbeitnehmeruberlassung, Direktvermittlung',
  });

  const renderImpressum = () => {
    if (lang === 'en') {
      // ...existing code for EN...
    } else if (lang === 'bs') {
      // ...existing code for BS...
    } else if (lang === 'de') {
      return (
        <>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Angaben gemäß § 5 TMG</h3>
          <p>
            <strong>ViaMedica Balkan</strong>
            <br />
            Geschäftsführer: <strong>Haris Samardzic</strong>
            <br />
            Personalvermittlung im Gesundheitswesen
            <br />
            Mutnik 232
            <br />
            77220 Cazin
            <br />
            Bosnien und Herzegowina
          </p>
          <p>
            <strong>Weitere ladungsfähige Anschrift in Deutschland:</strong>
            <br />
            Teisenbergstraße 1
            <br />
            83395 Freilassing
            <br />
            Deutschland
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Kontakt</h3>
          <p>
            Telefon (BA): +387 60 351 4325
            <br />
            Telefon (DE): +49 156 7868 0657
            <br />
            E-Mail: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Unternehmensgegenstand</h3>
          <p>
            ViaMedica Balkan erbringt Leistungen in den Bereichen Personalvermittlung und Arbeitnehmerüberlassung im Gesundheitswesen.
            <br />
            Wir bieten:
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Direktvermittlung von Pflegefach- und Hilfskräften aus dem Westbalkan an Arbeitgeber in Deutschland</li>
            <li>Direktvermittlung von Pflegekräften, die bereits in Deutschland tätig sind, an neue Arbeitgeber</li>
            <li>Arbeitnehmerüberlassung (ANÜ) im Rahmen des AÜG für kurzfristige und planbare Personalbedarfe</li>
          </ul>
          <p>
            Die Leistungen erfolgen nach den jeweils anwendbaren gesetzlichen Vorgaben.
            Im Fall der Direktvermittlung werden Fachkräfte direkt beim Arbeitgeber angestellt.
            Im Fall der Arbeitnehmerüberlassung bleiben die Fachkräfte beim Verleiher angestellt.
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Verantwortlich nach § 18 Abs. 2 MStV</h3>
          <p>
            Haris Samardzic
            <br />
            Mutnik 232
            <br />
            77220 Cazin
            <br />
            Bosnien und Herzegowina
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Haftung für Inhalte</h3>
          <p>
            Als Diensteanbieter sind wir gemäß § 7 Abs. 1 TMG für eigene Inhalte verantwortlich. Nach §§ 8–10 TMG sind wir jedoch nicht verpflichtet,
            übermittelte oder gespeicherte fremde Informationen zu überwachen. Bei Bekanntwerden von Rechtsverletzungen entfernen wir entsprechende
            Inhalte umgehend.
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Haftung für Links</h3>
          <p>
            Unser Angebot enthält Links zu externen Websites Dritter, auf deren Inhalte wir keinen Einfluss haben. Für die Inhalte der verlinkten Seiten
            ist stets der jeweilige Anbieter verantwortlich. Bei Bekanntwerden von Rechtsverletzungen entfernen wir solche Links umgehend.
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Online-Streitbeilegung / Verbraucherschlichtung</h3>
          <p>
            Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung bereit:
            <br />
            <a className="text-primary hover:underline" href="https://ec.europa.eu/consumers/odr/" target="_blank" rel="noopener noreferrer">
              https://ec.europa.eu/consumers/odr/
            </a>
            <br />
            Wir sind nicht bereit und nicht verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen.
          </p>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">Social-Media-Profile</h3>
          <ul className="list-disc pl-6 space-y-1">
             <li>Facebook</li>
             <li>Instagram</li>
             <li>Viber</li>
             <li>Telegram</li>
          </ul>
        </>
      );
    } else {
      return null;
    }
      }

  const renderPrivacy = () => {
    if (lang === 'en') {
      return (
        <>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Controller</h3>
          <p>
            ViaMedica Balkan
            <br />
            Managing Director: Haris Samardzic
            <br />
            Mutnik 232, 77220 Cazin, Bosnia and Herzegovina
            <br />
            Additional address in Germany: Teisenbergstraße 1, 83395 Freilassing
            <br />
            Email: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
            <br />
            Phone (BA): +387 60 351 4325
            <br />
            Phone (DE): +49 156 7868 0657
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. General information</h3>
          <p>
            We process personal data in accordance with the GDPR and applicable data protection laws.
            Processing takes place only where necessary to provide our services or to fulfill legal obligations.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Purposes of processing</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Temporary staffing (ANU/ANUG model) for healthcare employers in Germany</li>
            <li>Direct placement of nursing professionals from the Western Balkans</li>
            <li>Direct placement of nursing professionals already working in Germany</li>
            <li>Execution of application and recruitment processes</li>
            <li>Communication with candidates and employers</li>
            <li>Contract preparation and execution</li>
            <li>Operation of website and social media channels</li>
            <li>Fulfillment of legal obligations</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Categories of personal data</h3>
          <p>
            <strong>Candidate data:</strong> Name, contact details, qualifications, professional experience, language skills, application documents,
            career preferences, residence status, visa documents.
          </p>
          <p>
            <strong>Employer data:</strong> Contact details, company data, candidate requirements.
          </p>
          <p>
            <strong>Website and communication data:</strong> IP address, browser data, server logs, contact form data, emails, telephone and messenger communication.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4a. File uploads in the contact form</h3>
          <p>
            If you upload files in our contact form (e.g. CVs, certificates, supporting documents), these files are processed exclusively to handle your request
            and to evaluate suitability for temporary staffing and direct placement. Storage is provided via Supabase Storage (hosted infrastructure).
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Allowed formats: PDF, JPG, JPEG, PNG, DOC, DOCX</li>
            <li>Maximum file size: 5 MB per file</li>
            <li>Maximum number of files: 4 per request</li>
          </ul>
          <p>
            Legal basis: Art. 6(1)(a) GDPR (consent) and Art. 6(1)(b) GDPR (pre-contractual measures).
            Uploaded files are not used for advertising purposes and are only accessed by authorized personnel.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Legal bases</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Art. 6(1)(b) GDPR – contract performance</li>
            <li>Art. 6(1)(a) GDPR – consent</li>
            <li>Art. 6(1)(f) GDPR – legitimate interest</li>
            <li>Art. 6(1)(c) GDPR – legal obligation</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Data sharing</h3>
          <p>
            Data is shared exclusively with employers in Germany (with consent), authorities (e.g., immigration offices, embassies), and IT/hosting providers.
            No data is shared for advertising purposes.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Transfer to third countries</h3>
          <p>
            As we are established in Bosnia and Herzegovina, data may be transferred to a third country.
            Legal basis: Art. 49(1)(b) and Art. 49(1)(a) GDPR.
            We implement appropriate technical and organizational safeguards.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Storage period</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Candidate data: up to 24 months</li>
            <li>Contract and billing data: statutory retention periods</li>
            <li>Server logs: max. 30 days</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Data subject rights</h3>
          <p>
            Data subjects have the right to access, rectification, erasure, restriction of processing, data portability,
            objection, and withdrawal of consent at any time.
            Contact: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Server log files</h3>
          <p>
            When visiting the website, technical data is collected automatically (IP address, browser, timestamp, pages accessed).
            This data serves security purposes and is not merged with other data sources.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Contact forms and email</h3>
          <p>Data from contact requests is stored for processing and is not shared without consent.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">12. Social media profiles</h3>
          <p>
            We maintain profiles on Facebook, Instagram, Viber, and Telegram.
            When visiting these platforms, the privacy policies of the respective providers apply.
            We only process data actively provided by users.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">13. Cookies and tracking</h3>
          <p>Technically necessary cookies may be used. Analytics or marketing cookies are used only with consent.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">14. Data security</h3>
          <p>We implement technical and organizational measures to protect data against loss, misuse, and unauthorized access.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">15. Changes</h3>
          <p>We reserve the right to amend this privacy policy. The current version is always available on our website.</p>
        </>
      );
    }

    if (lang === 'bs') {
      return (
        <>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Odgovorno lice</h3>
          <p>
            ViaMedica Balkan
            <br />
            Direktor: Haris Samardzic
            <br />
            Mutnik 232, 77220 Cazin, Bosna i Hercegovina
            <br />
            Dodatna adresa u Njemačkoj: Teisenbergstraße 1, 83395 Freilassing
            <br />
            E-mail: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
            <br />
            Telefon (BA): +387 60 351 4325
            <br />
            Telefon (DE): +49 156 7868 0657
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. Opće informacije</h3>
          <p>
            Lične podatke obrađujemo u skladu s GDPR-om i važećim propisima o zaštiti podataka.
            Obrada se vrši samo u mjeri potrebnoj za pružanje naših usluga ili ispunjavanje zakonskih obaveza.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Svrhe obrade podataka</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Ustupanje radne snage (ANU model) za poslodavce u zdravstvu u Njemačkoj</li>
            <li>Direktno posredovanje medicinskih radnika iz zemalja Zapadnog Balkana</li>
            <li>Direktno posredovanje medicinskih radnika koji već rade u Njemačkoj</li>
            <li>Provođenje procesa prijave i zapošljavanja</li>
            <li>Komunikacija s kandidatima i poslodavcima</li>
            <li>Priprema i izvršenje ugovora</li>
            <li>Rad web-stranice i kanala društvenih mreža</li>
            <li>Ispunjavanje zakonskih obaveza</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Kategorije ličnih podataka</h3>
          <p>
            <strong>Podaci kandidata:</strong> ime, kontakt podaci, kvalifikacije, radno iskustvo, znanje jezika, dokumenti prijave,
            profesionalne želje, status boravka, vizna dokumentacija.
          </p>
          <p>
            <strong>Podaci poslodavaca:</strong> kontakt podaci, podaci o kompaniji, zahtjevi prema kandidatima.
          </p>
          <p>
            <strong>Podaci web-stranice i komunikacije:</strong> IP adresa, podaci o pregledniku, server logovi, podaci iz kontakt-formulara,
            e-mail komunikacija, telefonska i messenger komunikacija.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4a. Otpremanje datoteka putem kontakt-formulara</h3>
          <p>
            Ako putem kontakt-formulara otpremite datoteke (npr. CV, potvrde, prateća dokumenta), te datoteke obrađujemo isključivo radi obrade vašeg upita
            i procjene prikladnosti za ANU i direktno posredovanje. Pohrana se vrši putem Supabase Storage infrastrukture.
          </p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Dozvoljeni formati: PDF, JPG, JPEG, PNG, DOC, DOCX</li>
            <li>Maksimalna veličina: 5 MB po datoteci</li>
            <li>Maksimalan broj datoteka: 4 po upitu</li>
          </ul>
          <p>
            Pravna osnova: član 6 st. 1 tač. a GDPR (saglasnost) i član 6 st. 1 tač. b GDPR (predugovorne mjere).
            Otpremene datoteke se ne koriste u marketinške svrhe i dostupne su samo ovlaštenim osobama.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Pravna osnova</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Član 6 st. 1 tač. b GDPR – izvršenje ugovora</li>
            <li>Član 6 st. 1 tač. a GDPR – saglasnost</li>
            <li>Član 6 st. 1 tač. f GDPR – legitimni interes</li>
            <li>Član 6 st. 1 tač. c GDPR – zakonska obaveza</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Prosljeđivanje podataka</h3>
          <p>
            Podaci se prosljeđuju isključivo poslodavcima u Njemačkoj (uz saglasnost), nadležnim organima
            (npr. službama za strance, ambasadama) te IT i hosting pružaocima usluga.
            Podaci se ne prosljeđuju u marketinške svrhe.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Prenos podataka u treće zemlje</h3>
          <p>
            Budući da se nalazimo u Bosni i Hercegovini, može doći do prenosa podataka u treću zemlju.
            Pravna osnova: član 49 st. 1 tač. b i tač. a GDPR.
            Primjenjujemo odgovarajuće tehničke i organizacione mjere zaštite.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Period čuvanja podataka</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Podaci kandidata: do 24 mjeseca</li>
            <li>Ugovorni i obračunski podaci: prema zakonskim rokovima</li>
            <li>Server logovi: najviše 30 dana</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Prava ispitanika</h3>
          <p>
            Ispitanici imaju pravo na pristup, ispravku, brisanje, ograničenje obrade, prenosivost podataka,
            prigovor i povlačenje saglasnosti u svakom trenutku.
            Kontakt: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Server log datoteke</h3>
          <p>
            Prilikom posjete web-stranici automatski se bilježe tehnički podaci (IP adresa, preglednik, vrijeme, posjećene stranice).
            Ovi podaci služe sigurnosti i ne spajaju se s drugim izvorima podataka.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Kontakt formulari i e-mail</h3>
          <p>Podaci iz kontakt-upita čuvaju se radi obrade i ne prosljeđuju bez saglasnosti.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">12. Profili na društvenim mrežama</h3>
          <p>
            Imamo profile na Facebooku, Instagramu, Viberu i Telegramu.
            Pri posjeti tih platformi važe pravila zaštite podataka odgovarajućih pružalaca.
            Obrađujemo samo podatke koje korisnici aktivno dostave.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">13. Kolačići i praćenje</h3>
          <p>Mogu se koristiti tehnički nužni kolačići. Analitički i marketinški kolačići koriste se samo uz saglasnost.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">14. Sigurnost podataka</h3>
          <p>Primjenjujemo tehničke i organizacione mjere kako bismo zaštitili podatke od gubitka, zloupotrebe i neovlaštenog pristupa.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">15. Izmjene</h3>
          <p>Zadržavamo pravo izmjene ove izjave o zaštiti podataka. Aktuelna verzija je uvijek dostupna na našoj web-stranici.</p>
        </>
      );
    }

    return (
      <>
        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Verantwortlicher</h3>
        <p>
          ViaMedica Balkan
          <br />
          Geschäftsführer: Haris Samardzic
          <br />
          Mutnik 232, 77220 Cazin, Bosnien und Herzegowina
          <br />
          Weitere Anschrift in Deutschland: Teisenbergstraße 1, 83395 Freilassing
          <br />
          E-Mail: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
          <br />
          Telefon (BA): +387 60 351 4325
          <br />
          Telefon (DE): +49 156 7868 0657
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. Allgemeine Hinweise</h3>
        <p>
          Wir verarbeiten personenbezogene Daten gemäß DSGVO und den einschlägigen Datenschutzgesetzen. Die Verarbeitung erfolgt nur, soweit sie zur
          Erbringung unserer Dienstleistungen oder zur Erfüllung gesetzlicher Pflichten erforderlich ist.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Zwecke der Datenverarbeitung</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Arbeitnehmerüberlassung (ANÜ) für Arbeitgeber im Gesundheitswesen in Deutschland</li>
          <li>Direktvermittlung von Pflegekräften aus dem Westbalkan</li>
          <li>Direktvermittlung von Pflegekräften, die bereits in Deutschland tätig sind</li>
          <li>Durchführung von Bewerbungsprozessen</li>
          <li>Kommunikation mit Bewerbern und Arbeitgebern</li>
          <li>Vertragsvorbereitung und -durchführung</li>
          <li>Betrieb der Website und Social-Media-Kanäle</li>
          <li>Erfüllung gesetzlicher Pflichten</li>
        </ul>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Kategorien personenbezogener Daten</h3>
        <p>
          <strong>Bewerberdaten:</strong> Name, Kontaktdaten, Qualifikationen, Berufserfahrung, Sprachkenntnisse, Bewerbungsunterlagen, berufliche
          Wünsche, Aufenthaltsstatus, Visumsunterlagen.
        </p>
        <p>
          <strong>Arbeitgeberdaten:</strong> Kontaktdaten, Unternehmensdaten, Anforderungen an Bewerber.
        </p>
        <p>
          <strong>Website- und Kommunikationsdaten:</strong> IP-Adresse, Browserdaten, Server-Logfiles, Kontaktformulardaten, E-Mails, Telefon- und
          Messenger-Kommunikation.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4a. Datei-Uploads im Kontaktformular</h3>
        <p>
          Wenn Sie im Kontaktformular Dateien hochladen (z. B. Lebenslauf, Zeugnisse, Nachweise), verarbeiten wir diese Dateien ausschließlich zur
          Bearbeitung Ihrer Anfrage und zur Prüfung der Eignung für ANÜ und Direktvermittlung. Die Speicherung erfolgt über Supabase Storage.
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Erlaubte Formate: PDF, JPG, JPEG, PNG, DOC, DOCX</li>
          <li>Maximale Dateigröße: 5 MB pro Datei</li>
          <li>Maximale Anzahl: 4 Dateien pro Anfrage</li>
        </ul>
        <p>
          Rechtsgrundlagen: Art. 6 Abs. 1 lit. a DSGVO (Einwilligung) und Art. 6 Abs. 1 lit. b DSGVO (vorvertragliche Maßnahmen).
          Hochgeladene Dateien werden nicht zu Werbezwecken genutzt und sind nur für berechtigte Personen zugänglich.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Rechtsgrundlagen</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Art. 6 Abs. 1 lit. b DSGVO – Vertragsdurchführung</li>
          <li>Art. 6 Abs. 1 lit. a DSGVO – Einwilligung</li>
          <li>Art. 6 Abs. 1 lit. f DSGVO – berechtigtes Interesse</li>
          <li>Art. 6 Abs. 1 lit. c DSGVO – gesetzliche Pflichten</li>
        </ul>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Weitergabe von Daten</h3>
        <p>
          Eine Weitergabe erfolgt ausschließlich an Arbeitgeber in Deutschland (mit Einwilligung), Behörden (z. B. Ausländerbehörden, Botschaften)
          sowie IT- und Hosting-Dienstleister. Eine Weitergabe zu Werbezwecken findet nicht statt.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Datenübermittlung in Drittländer</h3>
        <p>
          Da wir in Bosnien und Herzegowina ansässig sind, erfolgt eine Datenübermittlung in ein Drittland. Rechtsgrundlagen: Art. 49 Abs. 1 lit. b
          DSGVO und Art. 49 Abs. 1 lit. a DSGVO. Wir treffen angemessene technische und organisatorische Schutzmaßnahmen.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Speicherdauer</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Bewerberdaten: bis zu 24 Monate</li>
          <li>Vertrags- und Abrechnungsdaten: gesetzliche Fristen</li>
          <li>Server-Logfiles: max. 30 Tage</li>
        </ul>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Rechte der betroffenen Personen</h3>
        <p>
          Betroffene Personen haben das Recht auf Auskunft, Berichtigung, Löschung, Einschränkung, Datenübertragbarkeit, Widerspruch sowie Widerruf
          einer Einwilligung. Kontakt: <a className="text-primary hover:underline" href="mailto:info@via-medica-balkan.de">info@via-medica-balkan.de</a>
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Server-Logfiles</h3>
        <p>
          Beim Besuch der Website werden automatisch technische Daten erfasst (IP-Adresse, Browser, Uhrzeit, aufgerufene Seiten). Diese dienen der
          Sicherheit und werden nicht mit anderen Datenquellen zusammengeführt.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Kontaktformulare und E-Mail</h3>
        <p>Daten aus Kontaktanfragen werden zur Bearbeitung gespeichert und nicht ohne Einwilligung weitergegeben.</p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">12. Social-Media-Profile</h3>
        <p>
           Wir betreiben Profile auf Facebook, Instagram, Viber und Telegram. Beim Besuch gelten die Datenschutzrichtlinien der jeweiligen Anbieter.
          Wir verarbeiten dort nur Daten, die Nutzer aktiv übermitteln.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">13. Cookies und Tracking</h3>
        <p>Technisch notwendige Cookies können eingesetzt werden. Analyse- oder Marketing-Cookies nur mit Einwilligung.</p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">14. Datensicherheit</h3>
        <p>
          Wir setzen technische und organisatorische Maßnahmen ein, um Daten vor Verlust, Missbrauch und unbefugtem Zugriff zu schützen.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">15. Änderungen</h3>
        <p>
          Wir behalten uns vor, diese Datenschutzerklärung anzupassen. Die aktuelle Version ist jederzeit auf unserer Website abrufbar.
        </p>
      </>
    );
  };

  const renderTerms = () => {
    if (lang === 'en') {
      return (
        <>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Scope</h3>
          <p>
            These General Terms and Conditions apply to all contracts, services, and offers provided by ViaMedica Balkan.
            They govern cooperation between the service provider and employers in Germany for temporary staffing and direct placement.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. Subject matter of services</h3>
          <p>The service provider offers the following services in the healthcare sector:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Direct placement of nursing professionals from the Western Balkans</li>
            <li>Direct placement of nursing professionals already working in Germany</li>
            <li>Temporary staffing under applicable legal requirements for agency work</li>
          </ul>
          <p>
            In direct placement, professionals are employed directly by the client.
            In temporary staffing, professionals remain employed by the provider while assigned to the client.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Contract conclusion</h3>
          <p>
            A contract is concluded by written agreement, electronic confirmation, or acceptance of a candidate profile/service offer.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Obligations of the placement provider</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Careful selection of suitable candidates</li>
            <li>Data sharing only with consent</li>
            <li>Transparent support throughout temporary staffing and placement processes</li>
            <li>Provision of relevant information about candidates</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Obligations of the client</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Provision of all relevant information</li>
            <li>Prompt feedback on hires or rejections</li>
            <li>No sharing of candidate data without consent</li>
            <li>Compliance with all legal requirements</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Remuneration</h3>
          <p>
            Remuneration is determined by the respective contract model (temporary staffing or direct placement).
            For direct placement, fees become due according to the placement agreement. For temporary staffing, billing is based on actual deployed hours.
            All prices are net plus statutory VAT where applicable.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Claims for reimbursement / withdrawal</h3>
          <p>
            No claim for reimbursement exists if the candidate resigns, is terminated by the client,
            or if reasons arise outside the placement provider’s sphere of influence.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Liability</h3>
          <p>
            The service provider is liable only for intent or gross negligence.
            No liability is assumed for candidate suitability, conduct, or decisions by public authorities.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Data protection</h3>
          <p>Personal data is processed in accordance with the GDPR and the placement provider’s privacy policy.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Confidentiality</h3>
          <p>All transmitted information must be treated as confidential and may not be disclosed to third parties.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Final provisions</h3>
          <p>
            German law applies where legally permissible. Where legally permissible, the place of jurisdiction is the client’s registered office.
            If any provision is invalid, the remaining provisions remain effective.
          </p>
        </>
      );
    }

    if (lang === 'bs') {
      return (
        <>
          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Područje primjene</h3>
          <p>
            Ovi opći uslovi poslovanja važe za sve ugovore, usluge i ponude društva ViaMedica Balkan.
            Oni uređuju saradnju između pružaoca usluge i poslodavaca u Njemačkoj za ANU i direktno posredovanje.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. Predmet usluge</h3>
          <p>Pružalac usluge u sektoru zdravstva nudi sljedeće modele saradnje:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Direktno posredovanje medicinskih radnika iz zemalja Zapadnog Balkana</li>
            <li>Direktno posredovanje medicinskih radnika koji već rade u Njemačkoj</li>
            <li>Ustupanje radne snage (ANU) u skladu s važećim zakonskim propisima</li>
          </ul>
          <p>
            Kod direktnog posredovanja medicinski radnici zasnivaju radni odnos direktno kod naručioca.
            Kod ANU modela radni odnos ostaje kod ustupioca, a radnik se upućuje na rad kod naručioca.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Zaključenje ugovora</h3>
          <p>
            Ugovor nastaje pisanim sporazumom, elektronskom potvrdom ili prihvatanjem profila kandidata/ponude usluge.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Obaveze posrednika</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Pažljiv odabir odgovarajućih kandidata</li>
            <li>Prosljeđivanje podataka isključivo uz saglasnost</li>
            <li>Transparentna pratnja kroz procese ANU i direktnog posredovanja</li>
            <li>Dostavljanje relevantnih informacija o kandidatima</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Obaveze naručioca</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Dostavljanje svih relevantnih informacija</li>
            <li>Brza povratna informacija o zapošljavanju ili odbijanju kandidata</li>
            <li>Zabrana prosljeđivanja podataka o kandidatima bez saglasnosti</li>
            <li>Poštivanje svih zakonskih propisa</li>
          </ul>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Naknada</h3>
          <p>
            Naknada se određuje prema ugovorenom modelu (ANU ili direktno posredovanje).
            Za direktno posredovanje primjenjuje se ugovorena provizija, a za ANU obračun prema stvarno odrađenim satima.
            Sve cijene su izražene bez PDV-a, ukoliko je primjenjivo.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Potraživanja naknade / odustanak</h3>
          <p>
            Ne postoji pravo na povrat naknade ako kandidat da otkaz, bude otpušten od strane naručioca
            ili ako razlozi nastanu izvan područja uticaja posrednika.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Odgovornost</h3>
          <p>
            Pružalac usluge odgovara samo za namjeru i grubu nepažnju.
            Ne preuzima se odgovornost za podobnost kandidata, njegovo ponašanje ili odluke nadležnih organa.
          </p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Zaštita podataka</h3>
          <p>Obrada ličnih podataka vrši se u skladu s GDPR-om i izjavom o zaštiti podataka posrednika.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Povjerljivost</h3>
          <p>Sve dostavljene informacije moraju se tretirati povjerljivo i ne smiju se prosljeđivati trećim stranama.</p>

          <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Završne odredbe</h3>
          <p>
            Primjenjuje se njemačko pravo u mjeri u kojoj je to dopušteno. Mjesto nadležnosti je, u mjeri dopuštenoj zakonom, sjedište naručioca.
            Ako pojedine odredbe budu nevažeće, ostale odredbe ostaju na snazi.
          </p>
        </>
      );
    }

    return (
      <>
        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">1. Geltungsbereich</h3>
        <p>
          Diese AGB gelten für alle Verträge, Leistungen und Angebote der ViaMedica Balkan. Sie regeln die Zusammenarbeit zwischen dem Vermittler und
          Arbeitgebern in Deutschland für Arbeitnehmerüberlassung (ANÜ) und Direktvermittlung.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">2. Leistungsgegenstand</h3>
        <p>Der Vermittler bietet im Gesundheitswesen folgende Leistungsmodelle an:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Direktvermittlung von Pflegekräften aus dem Westbalkan</li>
          <li>Direktvermittlung von Pflegekräften, die bereits in Deutschland tätig sind</li>
          <li>Arbeitnehmerüberlassung (ANÜ) im Rahmen der einschlägigen gesetzlichen Vorgaben</li>
        </ul>
        <p>
          Bei der Direktvermittlung werden Pflegekräfte direkt beim Auftraggeber angestellt.
          Bei der Arbeitnehmerüberlassung bleiben Pflegekräfte beim Verleiher angestellt und werden dem Auftraggeber überlassen.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">3. Vertragsabschluss</h3>
        <p>
          Ein Vertrag kommt zustande durch schriftliche Vereinbarung, elektronische Bestätigung oder durch Annahme eines Bewerberprofils bzw.
          Dienstleistungsangebots.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">4. Pflichten des Vermittlers</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Sorgfältige Auswahl geeigneter Bewerber</li>
          <li>Weitergabe von Daten nur mit Einwilligung</li>
          <li>Transparente Begleitung der Prozesse in ANÜ und Direktvermittlung</li>
          <li>Bereitstellung relevanter Informationen über Bewerber</li>
        </ul>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">5. Pflichten des Auftraggebers</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Bereitstellung aller relevanten Informationen</li>
          <li>Unverzügliche Rückmeldung über Einstellungen oder Absagen</li>
          <li>Keine Weitergabe von Bewerberdaten ohne Einwilligung</li>
          <li>Einhaltung aller gesetzlichen Vorgaben</li>
        </ul>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">6. Vergütung</h3>
        <p>
          Die Vergütung richtet sich nach dem vereinbarten Leistungsmodell (ANÜ oder Direktvermittlung).
          Für Direktvermittlung gilt die vereinbarte Provision, für ANÜ erfolgt die Abrechnung nach tatsächlich geleisteten Stunden. Alle Preise verstehen
          sich zzgl. gesetzlicher Umsatzsteuer, sofern anwendbar.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">7. Ersatzansprüche / Rücktritt</h3>
        <p>
          Ein Anspruch auf Rückzahlung besteht nicht, wenn der Bewerber kündigt, vom Auftraggeber gekündigt wird oder Gründe außerhalb des
          Einflussbereichs des Vermittlers vorliegen.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">8. Haftung</h3>
        <p>
          Der Vermittler haftet nur für vorsätzliches oder grob fahrlässiges Verhalten. Keine Haftung besteht für Eignung, Verhalten des Bewerbers
          oder behördliche Entscheidungen.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">9. Datenschutz</h3>
        <p>Die Verarbeitung personenbezogener Daten erfolgt gemäß DSGVO und der Datenschutzerklärung des Vermittlers.</p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">10. Vertraulichkeit</h3>
        <p>
          Alle übermittelten Informationen sind vertraulich zu behandeln und dürfen nicht an Dritte weitergegeben werden.
        </p>

        <h3 className="font-heading text-lg font-semibold border-l-4 border-primary pl-3">11. Schlussbestimmungen</h3>
        <p>
          Es gilt deutsches Recht, soweit gesetzlich zulässig. Gerichtsstand ist, soweit gesetzlich zulässig, der Sitz des Auftraggebers. Sollten einzelne Bestimmungen unwirksam sein,
          bleibt die Wirksamkeit der übrigen Regelungen unberührt.
        </p>
      </>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {isAgbStandalonePage ? (
        <AGBComparisonLayout />
      ) : (
        <main className="pt-24 pb-16 container mx-auto px-2 sm:px-4 max-w-5xl">
          <section className="max-w-4xl mx-auto">
          <div className="mb-4 rounded-lg border bg-muted/50 px-2 sm:px-4 py-3 text-sm text-[color:var(--foreground)]/80">{ui.notice}</div>
          <img src="/favicon.png" alt="ViaMedica Balkan Logo" className="w-[120px] sm:w-[180px] mx-auto mb-8" decoding="async" />

          <div className="space-y-5 flex flex-col sm:flex-row sm:space-y-0 sm:space-x-4">
            <details
              open={activeSection === 'impressum'}
              onToggle={(event) => {
                if (event.currentTarget.open) setActiveSection('impressum');
              }}
              className="rounded-xl border bg-card shadow-sm overflow-hidden"
            >
              <summary
                onClick={(event) => {
                  if (activeSection === 'impressum') event.preventDefault();
                }}
                className="cursor-pointer list-none px-6 py-5 pr-14 text-lg font-semibold text-[color:var(--primary-foreground)] bg-gradient-to-r from-foreground to-primary relative"
              >
                {ui.impressum}
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl">{activeSection === 'impressum' ? '−' : '+'}</span>
              </summary>
              <div className="px-2 sm:px-6 py-5 bg-muted/40 text-[color:var(--foreground)]/90 space-y-4 rounded-lg shadow-sm flex-1 min-w-0">
                <div className="max-h-[320px] sm:max-h-[420px] overflow-y-auto">
                  {renderImpressum()}
                </div>
              </div>
            </details>

            <details
              open={activeSection === 'datenschutz'}
              onToggle={(event) => {
                if (event.currentTarget.open) setActiveSection('datenschutz');
              }}
              className="rounded-xl border bg-card shadow-sm overflow-hidden"
            >
              <summary
                onClick={(event) => {
                  if (activeSection === 'datenschutz') event.preventDefault();
                }}
                className="cursor-pointer list-none px-6 py-5 pr-14 text-lg font-semibold text-primary-foreground bg-gradient-to-r from-foreground to-primary relative"
              >
                {ui.privacy}
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl">{activeSection === 'datenschutz' ? '−' : '+'}</span>
              </summary>
              <div className="px-2 sm:px-6 py-5 bg-muted/40 text-foreground/90 space-y-4 rounded-lg shadow-sm flex-1 min-w-0">
                <div className="max-h-[320px] sm:max-h-[420px] overflow-y-auto">
                  {renderPrivacy()}
                </div>
              </div>
            </details>

            <details
              open={activeSection === 'agb'}
              onToggle={(event) => {
                if (event.currentTarget.open) setActiveSection('agb');
              }}
              className="rounded-xl border border-border bg-card shadow-sm overflow-hidden"
            >
              <summary
                onClick={(event) => {
                  if (activeSection === 'agb') event.preventDefault();
                }}
                className="cursor-pointer list-none px-6 py-5 pr-14 text-lg font-semibold text-primary-foreground bg-gradient-to-r from-foreground to-primary relative"
              >
                {ui.terms}
                <span className="absolute right-6 top-1/2 -translate-y-1/2 text-xl">{activeSection === 'agb' ? '−' : '+'}</span>
              </summary>
              <div className="px-2 sm:px-6 py-5 bg-muted/40 text-foreground/90 space-y-4 rounded-lg shadow-sm flex-1 min-w-0">
                <div className="max-h-[320px] sm:max-h-[420px] overflow-y-auto">
                  {renderTerms()}
                </div>
              </div>
            </details>
          </div>
          </section>
        </main>
      )}

      <Footer />
    </div>
  );
};

export default Legal;
